/**
 * DateRangeSelect
 *
 * A date-range picker with preset shortcuts and dual-calendar display.
 *
 * Features:
 * - Trigger input shows the selected range as "MM/DD/YYYY - MM/DD/YYYY"
 * - Dropdown with preset range options (Last 30 days, This/Last Month, etc.)
 * - "Custom Range" mode for free-form calendar selection
 * - Two independent calendars (separately navigable) powered by Mantine DatePicker
 * - FROM / TO summary with Apply / Cancel workflow
 * - Fully controlled via value + onChange
 */

import { useCallback, useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Combobox,
  Divider,
  Group,
  Input,
  InputBase,
  Popover,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { IconCalendarEvent, IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  calendarDateFromPickerValue,
  calendarDateRangeFromPickerValue,
  formatCalendarDateUs,
  normalizeCalendarDateRange,
  parseCalendarDateUs,
  type CalendarDateRange,
} from '../lib/dateHelpers';

type DateRange = CalendarDateRange;

function formatDate(d: Date | null): string {
  const normalized = d ? calendarDateFromPickerValue(d) : null;
  return normalized ? formatCalendarDateUs(normalized) : '';
}

export interface DateRangePreset {
  label: string;
  getValue: () => DateRange;
}

const DEFAULT_PRESETS: DateRangePreset[] = [
  {
    label: 'Last 30 days',
    getValue: () => [
      dayjs().subtract(30, 'day').startOf('day').toDate(),
      dayjs().startOf('day').toDate(),
    ],
  },
  {
    label: 'This Month',
    getValue: () => [
      dayjs().startOf('month').toDate(),
      dayjs().endOf('month').startOf('day').toDate(),
    ],
  },
  {
    label: 'Last Month',
    getValue: () => [
      dayjs().subtract(1, 'month').startOf('month').toDate(),
      dayjs().subtract(1, 'month').endOf('month').startOf('day').toDate(),
    ],
  },
  {
    label: 'This Year',
    getValue: () => [
      dayjs().startOf('year').toDate(),
      dayjs().endOf('year').startOf('day').toDate(),
    ],
  },
  {
    label: 'Last Year',
    getValue: () => [
      dayjs().subtract(1, 'year').startOf('year').toDate(),
      dayjs().subtract(1, 'year').endOf('year').startOf('day').toDate(),
    ],
  },
];

const CUSTOM_RANGE_KEY = 'Custom Range';

export interface DateRangeSelectProps {
  value: DateRange;
  onChange: (value: DateRange) => void;
  label?: React.ReactNode;
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  w?: number | string;
  style?: React.CSSProperties;
  /** Override the built-in presets. */
  presets?: DateRangePreset[];
  disabled?: boolean;
  /** Show clear (X) button when a range is selected. @default true */
  clearable?: boolean;
}

export function DateRangeSelect({
  value,
  onChange,
  label,
  placeholder = 'Select date range',
  size,
  w,
  style,
  presets = DEFAULT_PRESETS,
  disabled,
  clearable = true,
}: DateRangeSelectProps): JSX.Element {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [leftMonth, setLeftMonth] = useState<Date>(
    () => value[0] ? dayjs(value[0]).startOf('month').toDate() : dayjs().startOf('month').toDate(),
  );
  const [rightMonth, setRightMonth] = useState<Date>(
    () => value[1]
      ? dayjs(value[1]).startOf('month').toDate()
      : dayjs().add(1, 'month').startOf('month').toDate(),
  );

  const handleLeftDateChange = useCallback((d: string | Date) => {
    setLeftMonth(new Date(d));
  }, []);
  const handleRightDateChange = useCallback((d: string | Date) => {
    setRightMonth(new Date(d));
  }, []);
  const [fromText, setFromText] = useState(() => formatDate(value[0]));
  const [toText, setToText] = useState(() => formatDate(value[1]));

  const applyDraft = useCallback((range: DateRange) => {
    setDraft(range);
    setFromText(formatDate(range[0]));
    setToText(formatDate(range[1]));
  }, []);

  const normalizedValue = useMemo(() => normalizeCalendarDateRange(value), [value]);

  const hasValue = !!(normalizedValue[0] || normalizedValue[1]);

  const displayText = useMemo(() => {
    if (!hasValue) return null;
    return `${formatDate(normalizedValue[0])} - ${formatDate(normalizedValue[1])}`;
  }, [normalizedValue, hasValue]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onChange([null, null]);
    },
    [onChange],
  );

  const handleOpen = useCallback(() => {
    applyDraft(normalizeCalendarDateRange(value));
    setActivePreset(null);
    const normalized = normalizeCalendarDateRange(value);
    setLeftMonth(
      normalized[0] ? dayjs(normalized[0]).startOf('month').toDate() : dayjs().startOf('month').toDate(),
    );
    setRightMonth(
      normalized[1]
        ? dayjs(normalized[1]).startOf('month').toDate()
        : dayjs().add(1, 'month').startOf('month').toDate(),
    );
    setOpened(true);
  }, [value, applyDraft]);

  const handleApply = useCallback(() => {
    onChange(normalizeCalendarDateRange(draft));
    setOpened(false);
  }, [draft, onChange]);

  const handleCancel = useCallback(() => {
    setOpened(false);
  }, []);

  const handlePreset = useCallback((preset: DateRangePreset) => {
    const range = preset.getValue();
    applyDraft(range);
    setActivePreset(preset.label);
    if (range[0]) {
      setLeftMonth(dayjs(range[0]).startOf('month').toDate());
    }
    if (range[1]) {
      setRightMonth(dayjs(range[1]).startOf('month').toDate());
    }
  }, [applyDraft]);

  const handleCustom = useCallback(() => {
    setActivePreset(CUSTOM_RANGE_KEY);
  }, []);

  const handleDatePickerChange = useCallback((range: [string | Date | null, string | Date | null]) => {
    applyDraft(calendarDateRangeFromPickerValue(range));
    setActivePreset(CUSTOM_RANGE_KEY);
  }, [applyDraft]);

  const handleFromChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.currentTarget.value;
    setFromText(text);
    const parsed = parseCalendarDateUs(text);
    if (parsed) {
      setDraft((prev) => [parsed, prev[1]]);
      setLeftMonth(dayjs(parsed).startOf('month').toDate());
      setActivePreset(CUSTOM_RANGE_KEY);
    }
  }, []);

  const handleToChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.currentTarget.value;
    setToText(text);
    const parsed = parseCalendarDateUs(text);
    if (parsed) {
      setDraft((prev) => [prev[0], parsed]);
      setRightMonth(dayjs(parsed).startOf('month').toDate());
      setActivePreset(CUSTOM_RANGE_KEY);
    }
  }, []);

  const handleFromBlur = useCallback(() => {
    setFromText(formatDate(draft[0]));
  }, [draft]);

  const handleToBlur = useCallback(() => {
    setToText(formatDate(draft[1]));
  }, [draft]);

  const normalizedDraft = useMemo(() => normalizeCalendarDateRange(draft), [draft]);

  return (
    <Box style={style} w={w}>
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        shadow="md"
        trapFocus
        width="auto"
      >
        <Popover.Target>
          <InputBase
            component="button"
            type="button"
            pointer
            label={label}
            size={size}
            leftSection={<IconCalendarEvent size={16} />}
            rightSection={
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                {clearable && hasValue && (
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={handleClear}
                    aria-label="Clear date range"
                  >
                    <IconX size={14} />
                  </ActionIcon>
                )}
                <Combobox.Chevron />
              </Group>
            }
            rightSectionWidth={clearable && hasValue ? 52 : 36}
            rightSectionPointerEvents={clearable && hasValue ? undefined : 'none'}
            onClick={handleOpen}
            disabled={disabled}
            styles={{ input: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}
          >
            {displayText ? (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                {displayText}
              </span>
            ) : (
              <Input.Placeholder>{placeholder}</Input.Placeholder>
            )}
          </InputBase>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Group align="stretch" gap={0} wrap="nowrap">
            {/* Presets sidebar */}
            <Stack
              gap={0}
              justify="space-between"
              style={{
                borderRight: '1px solid var(--mantine-color-gray-3)',
                minWidth: 170,
              }}
            >
              <Stack gap={0} pt={4}>
                {presets.map((p) => (
                  <UnstyledButton
                    key={p.label}
                    onClick={() => handlePreset(p)}
                    px="sm"
                    py={8}
                    display="block"
                    w="100%"
                    style={{
                      backgroundColor:
                        activePreset === p.label ? 'var(--mantine-color-gray-2)' : undefined,
                    }}
                  >
                    <Text
                      size="sm"
                      fw={activePreset === p.label ? 600 : 400}
                      c={activePreset === p.label ? 'var(--mantine-color-gray-9)' : 'var(--mantine-color-gray-7)'}
                    >
                      {p.label}
                    </Text>
                  </UnstyledButton>
                ))}
                <UnstyledButton
                  onClick={handleCustom}
                  px="sm"
                  py={8}
                  display="block"
                  w="100%"
                  style={{
                    backgroundColor:
                      activePreset === CUSTOM_RANGE_KEY ? 'var(--mantine-color-gray-2)' : undefined,
                  }}
                >
                  <Text
                    size="sm"
                    fw={activePreset === CUSTOM_RANGE_KEY ? 600 : 400}
                    c={
                      activePreset === CUSTOM_RANGE_KEY
                        ? 'var(--mantine-color-gray-9)'
                        : 'var(--mantine-color-gray-7)'
                    }
                  >
                    {CUSTOM_RANGE_KEY}
                  </Text>
                </UnstyledButton>
              </Stack>

              <Stack gap={0}>
                <Divider />
                <Group px="sm" py="xs" gap="xs" grow>
                  <div>
                    <Text size="xs" fw={600} c="dimmed" mb={2}>FROM</Text>
                    <TextInput
                      size="xs"
                      value={fromText}
                      onChange={handleFromChange}
                      onBlur={handleFromBlur}
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                  <div>
                    <Text size="xs" fw={600} c="dimmed" mb={2}>TO</Text>
                    <TextInput
                      size="xs"
                      value={toText}
                      onChange={handleToChange}
                      onBlur={handleToBlur}
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </Group>
                <Divider />
                <Group p="xs" gap="xs">
                  <Button size="xs" onClick={handleApply}>Apply</Button>
                  <Button size="xs" variant="default" onClick={handleCancel}>Cancel</Button>
                </Group>
              </Stack>
            </Stack>

            {/* Two independent calendars */}
            <Group gap={0} wrap="nowrap" align="flex-start">
              <Box p="sm">
                <DatePicker
                  type="range"
                  value={normalizedDraft}
                  onChange={handleDatePickerChange}
                  date={leftMonth}
                  onDateChange={handleLeftDateChange}
                  size="sm"
                />
              </Box>
              <Divider orientation="vertical" />
              <Box p="sm">
                <DatePicker
                  type="range"
                  value={normalizedDraft}
                  onChange={handleDatePickerChange}
                  date={rightMonth}
                  onDateChange={handleRightDateChange}
                  size="sm"
                />
              </Box>
            </Group>
          </Group>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
